import axios, {AxiosError, AxiosResponse} from 'axios';
import {ErrorCode, ErrorResponse, ServiceError} from '@/types/error.types';
import {AXIOS_TIMEOUT, INVALID_TOKEN_ERRORS} from '@/constants';
import {dispatchSnackbar} from '@/hooks';
import {isError} from '@epic-mw/error-types';

export const getApiPath = (): string => {
	return `${process.env.NEXT_PUBLIC_EPIC_SERVER_URL || ''}${process.env.NEXT_PUBLIC_API_PATH || '/api'}`;
};

export const getGraphQLPath = (): string => {
	return `${process.env.NEXT_PUBLIC_EPIC_SERVICE_GRAPHQL_URL}/graphql`;
};

const API = axios.create({
	baseURL: getApiPath(),
	withCredentials: true,
	timeout: AXIOS_TIMEOUT,
	headers: {
		Accept: 'application/json',
		'Content-Type': 'application/json'
	}
});

export const navigateToAuth = (path: 'login' | 'logout', params?: Record<string, any>) => {
	try {
		const url = new URL(`${process.env.NEXT_PUBLIC_EPIC_SERVER_URL}/api/id/${path}`);

		url.searchParams.set('lang', window._epicResolvedLocale);

		if (params) {
			for (const key in params) {
				url.searchParams.set(key, String(params[key]));
			}
		}

		if (!params?.redirectUrl) {
			url.searchParams.set('redirectUrl', window.location.href);
		}

		window.location.href = url.toString();
	} catch (err: unknown) {
		console.error(
			`navigateToAuth() Unable to navigate to ${path}: ${isError(err) ? err.message : 'unknown cause'}`
		);
	}
};

API.interceptors.response.use(
	async (response: AxiosResponse): Promise<AxiosResponse> => {
		/* eslint-disable */
		const gqlErrors: Array<{message: string; serviceResponse}> = response.data?.errors;
		if (Array.isArray(gqlErrors)) {
			for (const err of INVALID_TOKEN_ERRORS) {
				const gqlError = gqlErrors.find((e) => e.serviceResponse?.indexOf(err) > -1);
				if (gqlError) {
					console.error(
						`[API RESPONSE] ${response.config?.method}:${response.config?.url} : ${JSON.stringify(gqlError)}`
					);
					navigateToAuth('logout', {tokenInvalid: true});
					break;
				}
			}
		}
		/* eslint-enable */
		return response;
	},
	(error: AxiosError) => {
		const errData = error.response?.data as ServiceError & {error?: string};
		const errMessage = errData?.error || errData?.errorMessage || error.message;

		console.error(
			`[API ERROR] ${error.response?.status}:${error.config?.method}:${error.config?.url} : ${errMessage}`
		);

		const responseData = (error.response ? error.response.data : {}) as ErrorResponse;
		// normalize error
		responseData.error = errMessage;

		// Handle errors without a response / status (network issues, offline, etc.)
		if (!error.response) {
			switch (error.code) {
				case 'ERR_NETWORK':
				case 'ERR_INTERNET_DISCONNECTED': {
					console.warn('You are offline. Please check your internet connection.');
					dispatchSnackbar(ErrorCode.OFFLINE, 'critical');
					return Promise.reject({error: ErrorCode.OFFLINE});
				}

				case 'ECONNABORTED': {
					console.warn('Timeout issue.');
					dispatchSnackbar(ErrorCode.TIMEOUT, 'critical');
					return Promise.reject({error: ErrorCode.TIMEOUT});
				}

				default: {
					console.warn(`Unknown error: ${error.code || 'No code'}`);
					dispatchSnackbar(`Unknown error: ${error.code || 'No code'}`, 'critical');
					return Promise.reject({error: errMessage});
				}
			}
		}

		switch (error.response?.status) {
			case 401: {
				// tbd: maybe send to logout directly, check why we send to login again
				navigateToAuth('login', {tokenInvalid: true});
				return;
			}

			case 403: {
				if (INVALID_TOKEN_ERRORS.has(responseData?.error)) {
					navigateToAuth('logout', {sessionInvalidated: true});
					return;
				}
				break;
			}

			default: {
				if (process.env.NODE_ENV === 'development') {
					console.warn(
						`unhandled axios response error status ${error.response?.status}.`
					);
				}
				break;
			}
		}

		return Promise.reject(responseData);
	}
);

export {API};
